import React, { useState, useEffect } from 'react';
import { Save, Info } from 'lucide-react';
import { useChatbot } from '../../contexts/ChatbotContext';
import { useDesignColors } from '../../hooks/useDesignColors';
import { useChatbotLimitsHook } from '../../hooks/useChatbotLimits';

interface ChatbotLimitsProps {
  onUpdate?: () => void;
}

export function ChatbotMessageLimits({ onUpdate }: ChatbotLimitsProps) {
  const { chatbotPublicId } = useChatbot();
  const { colors } = useDesignColors();
  const { 
    messageLimit, 
    conversationLimit,
    loading, 
    updateMessageLimit, 
    updateConversationLimit 
  } = useChatbotLimitsHook();
  
  const [localMessageLimit, setLocalMessageLimit] = useState<number>(100);
  const [localConversationLimit, setLocalConversationLimit] = useState<number>(100);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Update local state when limits are fetched
  useEffect(() => {
    if (messageLimit) {
      setLocalMessageLimit(messageLimit);
    }
    if (conversationLimit) {
      setLocalConversationLimit(conversationLimit);
    }
  }, [messageLimit, conversationLimit]);

  const handleSave = async () => {
    try {
      setError(null);
      setSuccess(false);
      
      // Update both limits
      const messageResult = await updateMessageLimit(localMessageLimit);
      const conversationResult = await updateConversationLimit(localConversationLimit);
      
      if (messageResult && conversationResult) {
        setSuccess(true);
        if (onUpdate) {
          onUpdate();
        }
        
        // Hide success message after 3 seconds
        setTimeout(() => {
          setSuccess(false);
        }, 3000);
      } else {
        setError('Failed to update one or more limits');
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An error occurred');
    }
  };

  return (
    <div className="bg-white/5 backdrop-blur-xl rounded-xl p-6 border border-white/10">
      <h3 className="text-lg font-semibold text-white mb-6">Chatbot Conversation Limits</h3>
      
      <div className="space-y-6">
        {error && (
          <div className="bg-red-500/10 text-red-400 p-4 rounded-lg flex items-center gap-2">
            <Info className="w-5 h-5" />
            <p>{error}</p>
          </div>
        )}
        
        {success && (
          <div className="bg-green-500/10 text-green-400 p-4 rounded-lg flex items-center gap-2">
            <Info className="w-5 h-5" />
            <p>Limits updated successfully</p>
          </div>
        )}
        
        <div>
          <label className="block text-sm font-medium text-gray-300 mb-2">
            Maximum messages per conversation
          </label>
          <div className="flex items-center gap-4">
            <input
              type="number"
              min="1"
              max="1000"
              value={localMessageLimit}
              onChange={(e) => setLocalMessageLimit(parseInt(e.target.value) || 100)}
              className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-white focus:outline-none focus:ring-2 focus:ring-indigo-500"
            />
            <div className="text-gray-400 text-sm whitespace-nowrap">
              messages
            </div>
          </div>
          <p className="mt-1 text-sm text-gray-400">
            Limit the number of messages in a single conversation. After this limit is reached, users will be prompted to start a new conversation.
          </p>
        </div>
        
        <div>
          <label className="block text-sm font-medium text-gray-300 mb-2">
            Maximum conversations per month
          </label>
          <div className="flex items-center gap-4">
            <input
              type="number"
              min="1"
              max="1000"
              value={localConversationLimit}
              onChange={(e) => setLocalConversationLimit(parseInt(e.target.value) || 100)}
              className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-white focus:outline-none focus:ring-2 focus:ring-indigo-500"
            />
            <div className="text-gray-400 text-sm whitespace-nowrap">
              conversations
            </div>
          </div>
          <p className="mt-1 text-sm text-gray-400">
            Limit the number of conversations per month for this chatbot. This is separate from your account's global conversation limits.
          </p>
        </div>
        
        <button
          onClick={handleSave}
          disabled={loading}
          className="flex items-center gap-2 px-4 py-2 rounded-lg transition-colors"
          style={{
            background: `linear-gradient(135deg, ${colors.gradientStart}20, ${colors.gradientEnd}20)`,
            color: colors.gradientStart
          }}
        >
          <Save className="w-4 h-4" />
          {loading ? 'Saving...' : 'Save Limits'}
        </button>
      </div>
    </div>
  );
}